import type { TransactionQuery } from '../../api/client'
import { TX_STATUSES, type TxStatus } from '../../api/types'
import type { BusinessTime } from '../../time/businessTime'

/**
 * Filters live in the URL, not in component state.
 *
 * That is the highest-value structural difference from the screen this
 * replaces: a filtered view becomes shareable and bookmarkable, the browser's
 * back button works, and a reload does not silently discard the filter someone
 * is reconciling against. It also means the filter set has exactly one home,
 * so "what is the table currently showing" is answerable by reading the
 * address bar.
 *
 * Everything here is total and defensive, because a URL is user input: a
 * hand-edited `?page=-4&sort_by=drop+table` must produce a valid query, not a
 * crash and not a request the API will reject.
 */

// PRD FR-15: "Default page size: 50 rows. Configurable up to 500 rows per
// page." The API has always accepted 500 (MAX_PAGE_SIZE); the UI stopped at
// 200, so the size the spec promises was unreachable from the screen.
export const PAGE_SIZES = [25, 50, 100, 200, 500] as const
export const DEFAULT_PAGE_SIZE = 50

export type SortBy = 'date' | 'amount'
export type SortDir = 'asc' | 'desc'

/** The URL shape. All strings, because that is what a query string holds. */
export interface FilterSearch {
  readonly from?: string
  readonly to?: string
  readonly status?: string
  readonly min?: string
  readonly max?: string
  readonly ptn?: string
  readonly provider_txid?: string
  readonly gateway?: string
  readonly biller?: string
  readonly beneficiary?: string
  readonly service_type?: string
  readonly service_label?: string
  readonly page?: string
  readonly size?: string
  readonly sort?: string
  readonly dir?: string
}

/** A bound: a calendar day, or a day with a time to the minute or second (FR-41). */
const ISO_BOUND = /^\d{4}-\d{2}-\d{2}(T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?)?$/

const asBound = (value: string | undefined, fallback: string): string =>
  value && ISO_BOUND.test(value) ? value : fallback

/** The day part of a bound. */
export const boundDate = (bound: string): string => bound.slice(0, 10)

/** The calendar day after an ISO date — settlement day D starts on D-1, so
 *  a From bound on D-1 reads back as settlement day D. */
export const nextDay = (isoDate: string): string => {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** The calendar day before an ISO date — settlement day D is composed as a
 *  From bound on D-1 at the cut-off. Calendar arithmetic only, no timezone. */
export const previousDay = (isoDate: string): string => {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

const pad = (n: number) => String(n).padStart(2, '0')

/** "18:00" → "17:59:59": the last second before a cut-off, the To time of a
 *  settlement day. A midnight cut-off wraps to 23:59:59 of the day before. */
export const secondBefore = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number)
  const total = ((h ?? 0) * 60 + (m ?? 0)) * 60 - 1
  const wrapped = (total + 86400) % 86400
  return `${pad(Math.floor(wrapped / 3600))}:${pad(Math.floor((wrapped % 3600) / 60))}:${pad(wrapped % 60)}`
}

/** The time part of a bound as HH:MM:SS, or '' for a bare date. */
export const boundTime = (bound: string): string => {
  const t = bound.slice(11)
  if (!t) return ''
  return t.length === 5 ? `${t}:00` : t
}

/** Composes a bound: a bare date when the time is the whole-day default for
 *  that end, else the exact instant (seconds dropped when zero). */
export function composeBound(date: string, time: string, end: 'from' | 'to'): string {
  const whole = end === 'from' ? '00:00:00' : '23:59:59'
  if (!time || time === whole || (end === 'from' && time === '00:00')) return date
  const hhmmss = time.length === 5 ? `${time}:00` : time
  return hhmmss.endsWith(':00') ? `${date}T${hhmmss.slice(0, 5)}` : `${date}T${hhmmss}`
}

/** A bound as a comparable instant string, whole days expanded per end. */
const asInstant = (bound: string, end: 'from' | 'to'): string =>
  bound.length === 10 ? `${bound}T${end === 'from' ? '00:00:00' : '23:59:59'}` : bound.length === 16 ? `${bound}:00` : bound

/** Non-negative decimal, or empty. Anything else is dropped rather than sent
 *  to an API that would answer 400 for a URL the user did not knowingly type. */
const asAmount = (value: string | undefined): string =>
  value && /^\d+(\.\d+)?$/.test(value) ? value : ''

const asText = (value: string | undefined): string => (value ?? '').trim().slice(0, 64)

/** A service label, or a provider's transaction id: free text the API
 *  accepts up to 255 characters. */
const asLabel = (value: string | undefined): string => (value ?? '').trim().slice(0, 255)

/** A service type is a code the API refuses in any other shape, so anything
 *  else is dropped rather than sent. */
const asServiceType = (value: string | undefined): string => {
  const code = (value ?? '').trim().toUpperCase()
  return /^[A-Z0-9_]{1,64}$/.test(code) ? code : ''
}

/**
 * Gateway ids from the URL.
 *
 * Deliberately not validated against the user's assigned gateways here. The
 * server intersects whatever arrives with what the caller is entitled to, so
 * an id they do not hold produces an empty table — and doing the check here as
 * well would mean the UI silently rewriting a URL someone pasted, which hides
 * the reason they see nothing.
 */
const asGateways = (value: string | undefined): readonly string[] => {
  if (!value) return []
  return [...new Set(value.split(',').map((g) => g.trim()).filter(Boolean))].slice(0, 50)
}

const asPositiveInt = (value: string | undefined, fallback: number): number => {
  const n = Number(value)
  return Number.isInteger(n) && n >= 1 ? n : fallback
}

const asPageSize = (value: string | undefined): number => {
  const n = Number(value)
  return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE
}

const asStatuses = (value: string | undefined): readonly TxStatus[] => {
  if (!value) return []
  const valid = new Set<string>(TX_STATUSES)
  // Deduplicated: `?status=FAILED,FAILED` would otherwise reach the API twice
  // and make the query key differ from an equivalent single-value filter,
  // splitting the cache for no reason.
  return [...new Set(value.split(',').map((s) => s.trim().toUpperCase()))].filter((s): s is TxStatus =>
    valid.has(s),
  )
}

/**
 * The API defaults `date_from` to the first of the current month and `date_to`
 * to today — in UTC (`biller-api/internal/httpapi/filters.go:26-31`). The SPA sends an
 * explicit range derived in BUSINESS time instead, so the default view matches
 * the days a biller actually reconciles.
 *
 * The upstream UTC parsing is a known defect raised with the backend
 * workstream and deliberately NOT compensated for here: shifting our values to
 * cancel it out would make the bug invisible and permanent.
 */
export function defaultRange(businessTime: BusinessTime, now: Date): { from: string; to: string } {
  const today = businessTime.isoDate(now)
  const firstOfMonth = `${today.slice(0, 7)}-01`
  return { from: firstOfMonth, to: today }
}

export interface Filters {
  readonly from: string
  readonly to: string
  readonly statuses: readonly TxStatus[]
  readonly min: string
  readonly max: string
  readonly ptn: string
  /** MPAY-33568: exact search on the provider's transaction id, offered to
   *  every requester (AB-39). */
  readonly providerTxid: string
  readonly gateways: readonly string[]
  /** A biller code to narrow to, internal users in all-billers mode only.
   *  Like the gateway filter it is intersected server-side and can only
   *  narrow, so it needs no validation here either. */
  readonly biller: string
  /** Free text on the beneficiary identity: an internal user acting for a
   *  beneficiary organisation only (FR-42, AB-40).
   *  The server refuses it for anyone else; here it is just carried. */
  readonly beneficiary: string
  /** One service type and one biller-facing service label to narrow to
   *  (MPAY-33571). Service type is grantable (FR-38): the server refuses it to
   *  a caller it is withheld from, so the bar offers it only under a grant. */
  readonly serviceType: string
  readonly serviceLabel: string
  readonly page: number
  readonly size: number
  readonly sortBy: SortBy
  readonly sortDir: SortDir
}

export function parseFilters(search: FilterSearch, businessTime: BusinessTime, now: Date): Filters {
  const range = defaultRange(businessTime, now)
  const from = asBound(search.from, range.from)
  let to = asBound(search.to, range.to)
  // A range the API would reject with a 400 is corrected here rather than
  // round-tripped: the user typed a URL, not a bug report. Compared as
  // instants, so "25 Aug 09:00 → 25 Aug (whole day)" stands.
  if (asInstant(to, 'to') < asInstant(from, 'from')) to = boundDate(from)
  return {
    from,
    to,
    statuses: asStatuses(search.status),
    min: asAmount(search.min),
    max: asAmount(search.max),
    ptn: asText(search.ptn),
    providerTxid: asLabel(search.provider_txid),
    gateways: asGateways(search.gateway),
    biller: asText(search.biller),
    beneficiary: asText(search.beneficiary),
    serviceType: asServiceType(search.service_type),
    serviceLabel: asLabel(search.service_label),
    page: asPositiveInt(search.page, 1),
    size: asPageSize(search.size),
    sortBy: search.sort === 'amount' ? 'amount' : 'date',
    sortDir: search.dir === 'asc' ? 'asc' : 'desc',
  }
}

/** Only non-default values are written back, so a pristine view has a clean
 *  URL and a shared link carries exactly the filters that were chosen. */
export function toSearch(filters: Filters, businessTime: BusinessTime, now: Date): FilterSearch {
  const range = defaultRange(businessTime, now)
  const search: Record<string, string> = {}
  if (filters.from !== range.from) search.from = filters.from
  if (filters.to !== range.to) search.to = filters.to
  if (filters.statuses.length) search.status = filters.statuses.join(',')
  if (filters.min) search.min = filters.min
  if (filters.max) search.max = filters.max
  if (filters.ptn) search.ptn = filters.ptn
  if (filters.providerTxid) search.provider_txid = filters.providerTxid
  if (filters.gateways.length) search.gateway = filters.gateways.join(',')
  if (filters.biller) search.biller = filters.biller
  if (filters.beneficiary) search.beneficiary = filters.beneficiary
  if (filters.serviceType) search.service_type = filters.serviceType
  if (filters.serviceLabel) search.service_label = filters.serviceLabel
  if (filters.page !== 1) search.page = String(filters.page)
  if (filters.size !== DEFAULT_PAGE_SIZE) search.size = String(filters.size)
  if (filters.sortBy !== 'date') search.sort = filters.sortBy
  if (filters.sortDir !== 'desc') search.dir = filters.sortDir
  return search
}

export const toQuery = (filters: Filters): TransactionQuery => ({
  date_from: filters.from,
  date_to: filters.to,
  status: filters.statuses,
  amount_min: filters.min,
  amount_max: filters.max,
  ptn: filters.ptn,
  provider_txid: filters.providerTxid,
  gateway: filters.gateways,
  biller: filters.biller,
  beneficiary: filters.beneficiary,
  service_type: filters.serviceType,
  service_label: filters.serviceLabel,
  page: filters.page,
  page_size: filters.size,
  sort_by: filters.sortBy,
  sort_dir: filters.sortDir,
})

/** True when the user has narrowed anything beyond the default date range —
 *  which is what distinguishes "no results for this filter" from "no data at
 *  all", the two empty states that must not look alike. */
export const isNarrowed = (filters: Filters, businessTime: BusinessTime, now: Date): boolean => {
  const range = defaultRange(businessTime, now)
  return (
    filters.statuses.length > 0 ||
    filters.min !== '' ||
    filters.max !== '' ||
    filters.ptn !== '' ||
    filters.providerTxid !== '' ||
    filters.gateways.length > 0 ||
    filters.biller !== '' ||
    filters.beneficiary !== '' ||
    filters.serviceType !== '' ||
    filters.serviceLabel !== '' ||
    filters.from !== range.from ||
    filters.to !== range.to
  )
}

/** Any change other than paging resets to page 1 — otherwise a filter that
 *  narrows to two pages while the user is on page 5 shows an empty table with
 *  no explanation. */
export const withFilterChange = (filters: Filters, changes: Partial<Filters>): Filters => ({
  ...filters,
  ...changes,
  page: 'page' in changes ? (changes.page ?? 1) : 1,
})
